/**
 * Team Members slide-over — manages staff + clients on a single
 * project. Opened from the Project Detail → Party tab's team chip
 * card.
 *
 * Mirrors the mobile Team screen at
 *   /Users/gudupuramesh/Documents/siteexpens/app/(app)/projects/[id]/members.tsx
 *
 * Sections:
 *   - Members (joined) — `project.memberIds` + `project.clientUids`
 *     joined to `organizations/{orgId}/memberPublic`
 *   - Pending invites — `organizations/{orgId}/pendingInvites` filtered
 *     by `projectIds.includes(projectId)`
 *   - "+ Add team member" CTA → opens inline form (name + phone + role)
 *
 * Actions:
 *   - Add — calls `inviteMember({ orgId, phoneNumber, role,
 *           projectIds: [projectId], displayName })`
 *   - Edit role on joined member — calls `setMemberRole({ orgId, uid, role })`
 *   - Edit role on pending invite — re-calls `inviteMember` (server
 *     idempotent, replaces the pending row's role)
 *   - Remove joined member from THIS project only — direct Firestore
 *     write `arrayRemove(uid)` on `memberIds` or `clientUids`
 *   - Cancel pending invite — `removeMember({ orgId, phoneNumber })`
 *
 * All permission / capacity / role-hierarchy checks live server-
 * side. The slide-over only surfaces errors from the callable.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ASSIGNABLE_ROLES,
  ROLE_LABELS,
  useProjectMembers,
  type AssignableRole,
  type ProjectMember,
  type RoleKey,
} from '@/lib/useProjectMembers';
import {
  inviteProjectMember,
  normalizeIndianPhoneE164,
  removeProjectMember,
  removeUidFromProject,
  setMemberRoleCall,
} from '@/lib/teamMembers';
import { usePendingInvites, type PendingInvite } from '@/lib/usePendingInvites';

// ───── Display helpers ─────

function formatPhone(raw: string | null): string {
  if (!raw) return '';
  // +91 98765 43210 — assumes E.164 +91XXXXXXXXXX shape
  if (raw.startsWith('+91') && raw.length === 13) {
    return `+91 ${raw.slice(3, 8)} ${raw.slice(8)}`;
  }
  return raw;
}

function initials(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
}

function roleColor(role: RoleKey | null): { bg: string; fg: string } {
  switch (role) {
    case 'superAdmin':
      return { bg: '#FEF2F2', fg: '#B91C1C' };
    case 'admin':
      return { bg: '#FEF3C7', fg: '#92400E' };
    case 'manager':
      return { bg: '#DBEAFE', fg: '#1D4ED8' };
    case 'accountant':
      return { bg: '#E0E7FF', fg: '#3730A3' };
    case 'siteEngineer':
      return { bg: '#D1FAE5', fg: '#065F46' };
    case 'supervisor':
      return { bg: '#FFEDD5', fg: '#9A3412' };
    case 'designer':
      return { bg: '#FCE7F3', fg: '#BE185D' };
    case 'viewer':
      return { bg: '#F3F4F6', fg: '#374151' };
    case 'client':
      return { bg: '#F5F3FF', fg: '#6D28D9' };
    default:
      return { bg: '#F5F5F5', fg: '#525252' };
  }
}

// ───── Component ─────

type EditTarget =
  | { kind: 'invite' }
  | { kind: 'editMember'; member: ProjectMember }
  | { kind: 'editInvite'; invite: PendingInvite }
  | null;

/** Detect the `plan_limit_members` error thrown by `inviteMember`
 *  so we can render an Upgrade-plan CTA instead of a plain red
 *  banner. The server stuffs details onto the HttpsError; the web
 *  SDK exposes them as `error.details` (typed loosely as unknown). */
type PlanLimitDetails = {
  reason: 'plan_limit_members';
  tier: string;
  limit: number;
};

function parsePlanLimit(e: unknown): PlanLimitDetails | null {
  const err = e as { details?: unknown; code?: string } | null;
  if (!err) return null;
  const d = err.details as Record<string, unknown> | undefined;
  if (!d || typeof d !== 'object') return null;
  if (d.reason !== 'plan_limit_members') return null;
  return {
    reason: 'plan_limit_members',
    tier: typeof d.tier === 'string' ? d.tier : 'current',
    limit: typeof d.limit === 'number' ? d.limit : 0,
  };
}

export function TeamMembersSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  canManage = true,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  /** When false, the slide-over renders read-only (no add / edit /
   *  remove). Wire this to the caller's role gate. */
  canManage?: boolean;
}) {
  const { members, loading: membersLoading } = useProjectMembers(
    open ? projectId : undefined,
  );
  const { invites: allInvites, loading: invitesLoading } = usePendingInvites(
    open ? orgId : null,
  );
  const pendingForProject = useMemo(
    () => allInvites.filter((i) => i.projectIds.includes(projectId)),
    [allInvites, projectId],
  );

  const navigate = useNavigate();
  const [editing, setEditing] = useState<EditTarget>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planLimit, setPlanLimit] = useState<PlanLimitDetails | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<
    | { kind: 'member'; member: ProjectMember }
    | { kind: 'invite'; invite: PendingInvite }
    | null
  >(null);

  // Body scroll lock + Esc close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, busy]);

  // Reset transient state on close
  useEffect(() => {
    if (!open) {
      setEditing(null);
      setError(null);
      setPlanLimit(null);
      setConfirmRemove(null);
    }
  }, [open]);

  const onCancelInvite = async (invite: PendingInvite) => {
    if (!orgId) return;
    setBusy(true);
    setError(null);
    try {
      await removeProjectMember({ orgId, phoneNumber: invite.phoneNumber });
      setConfirmRemove(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onRemoveMemberFromProject = async (member: ProjectMember) => {
    setBusy(true);
    setError(null);
    try {
      await removeUidFromProject({
        projectId,
        uid: member.uid,
        asClient: member.isProjectClient,
      });
      setConfirmRemove(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const empty =
    !membersLoading &&
    !invitesLoading &&
    members.length === 0 &&
    pendingForProject.length === 0;

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-users-three" /> Team Members
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {/* Add CTA */}
          {canManage ? (
            editing?.kind === 'invite' ? (
              <InviteForm
                onCancel={() => setEditing(null)}
                onSubmit={async ({ phoneNumber, displayName, role }) => {
                  if (!orgId) {
                    setError('No organisation in scope.');
                    return;
                  }
                  setBusy(true);
                  setError(null);
                  setPlanLimit(null);
                  try {
                    await inviteProjectMember({
                      orgId,
                      phoneNumber,
                      role,
                      projectIds: [projectId],
                      displayName: displayName || undefined,
                    });
                    setEditing(null);
                  } catch (e) {
                    const limit = parsePlanLimit(e);
                    if (limit) setPlanLimit(limit);
                    else setError((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
                busy={busy}
              />
            ) : (
              <button
                type="button"
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => {
                  setError(null);
                  setEditing({ kind: 'invite' });
                }}
              >
                <i className="ph-bold ph-plus" /> Add team member
              </button>
            )
          ) : null}

          {planLimit ? (
            <div
              style={{
                background: 'var(--warning-bg)',
                border: '1px solid var(--warning)',
                borderRadius: 8,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <i
                  className="ph-bold ph-warning-circle"
                  style={{
                    color: 'var(--warning)',
                    fontSize: 18,
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: 'var(--text-primary)',
                      marginBottom: 4,
                    }}
                  >
                    Your {planLimit.tier} plan caps team at{' '}
                    {planLimit.limit} member
                    {planLimit.limit === 1 ? '' : 's'}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      lineHeight: 1.5,
                    }}
                  >
                    The cap is organisation-wide (not per project) and
                    includes pending invites. Upgrade to a paid plan to
                    add more — or invite this person with the{' '}
                    <b>Client</b> role instead (clients don't count
                    toward the cap).
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setPlanLimit(null)}
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    onClose();
                    navigate('/billing');
                  }}
                >
                  <i className="ph-bold ph-sparkle" /> Upgrade options
                </button>
              </div>
            </div>
          ) : null}

          {error ? (
            <div
              style={{
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Members */}
          {members.length > 0 ? (
            <section>
              <SectionLabel>
                Members · {members.length}
              </SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {members.map((m) =>
                  editing?.kind === 'editMember' &&
                  editing.member.uid === m.uid ? (
                    <RoleEditCard
                      key={m.uid}
                      current={m.role}
                      displayName={m.displayName}
                      phone={formatPhone(m.phoneNumber)}
                      onCancel={() => setEditing(null)}
                      onSave={async (role) => {
                        if (!orgId) return;
                        setBusy(true);
                        setError(null);
                        try {
                          await setMemberRoleCall({
                            orgId,
                            uid: m.uid,
                            role,
                          });
                          setEditing(null);
                        } catch (e) {
                          const limit = parsePlanLimit(e);
                          if (limit) setPlanLimit(limit);
                          else setError((e as Error).message);
                        } finally {
                          setBusy(false);
                        }
                      }}
                      onRemove={
                        canManage
                          ? () =>
                              setConfirmRemove({ kind: 'member', member: m })
                          : undefined
                      }
                      busy={busy}
                    />
                  ) : (
                    <MemberRow
                      key={m.uid}
                      member={m}
                      canManage={canManage}
                      onEdit={() =>
                        setEditing({ kind: 'editMember', member: m })
                      }
                    />
                  ),
                )}
              </div>
            </section>
          ) : null}

          {/* Pending invites */}
          {pendingForProject.length > 0 ? (
            <section>
              <SectionLabel>
                Pending invites · {pendingForProject.length}
              </SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pendingForProject.map((i) =>
                  editing?.kind === 'editInvite' &&
                  editing.invite.phoneNumber === i.phoneNumber ? (
                    <RoleEditCard
                      key={i.phoneNumber}
                      current={i.role}
                      displayName={i.displayName || i.phoneNumber}
                      phone={formatPhone(i.phoneNumber) + ' · Invited'}
                      onCancel={() => setEditing(null)}
                      onSave={async (role) => {
                        if (!orgId) return;
                        setBusy(true);
                        setError(null);
                        try {
                          await inviteProjectMember({
                            orgId,
                            phoneNumber: i.phoneNumber,
                            role,
                            projectIds: [projectId],
                            displayName: i.displayName ?? undefined,
                          });
                          setEditing(null);
                        } catch (e) {
                          const limit = parsePlanLimit(e);
                          if (limit) setPlanLimit(limit);
                          else setError((e as Error).message);
                        } finally {
                          setBusy(false);
                        }
                      }}
                      onRemove={
                        canManage
                          ? () =>
                              setConfirmRemove({ kind: 'invite', invite: i })
                          : undefined
                      }
                      busy={busy}
                    />
                  ) : (
                    <InviteRow
                      key={i.phoneNumber}
                      invite={i}
                      canManage={canManage}
                      onEdit={() =>
                        setEditing({ kind: 'editInvite', invite: i })
                      }
                    />
                  ),
                )}
              </div>
            </section>
          ) : null}

          {empty ? (
            <div
              style={{
                padding: 48,
                textAlign: 'center',
                color: 'var(--text-secondary)',
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>
                <i className="ph ph-users-three" />
              </div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                No team members yet
              </div>
              <div style={{ fontSize: 13 }}>
                Invite the people who'll work on this project — Designers,
                Engineers, the Client. They'll get an SMS link to join.
              </div>
            </div>
          ) : null}

          {/* Confirm remove */}
          {confirmRemove ? (
            <div
              style={{
                background: 'var(--critical-bg)',
                padding: 16,
                borderRadius: 8,
                border: '1px solid var(--critical)',
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--critical-text)',
                  marginBottom: 8,
                }}
              >
                {confirmRemove.kind === 'member'
                  ? `Remove ${confirmRemove.member.displayName} from this project?`
                  : `Cancel invite to ${
                      confirmRemove.invite.displayName ||
                      confirmRemove.invite.phoneNumber
                    }?`}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginBottom: 12,
                  lineHeight: 1.5,
                }}
              >
                {confirmRemove.kind === 'member'
                  ? 'They will lose access to this project only. They stay in the organisation for other projects.'
                  : 'The pending invite will be cancelled. They can be invited again later.'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmRemove(null)}
                  disabled={busy}
                >
                  Keep
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  style={{
                    background: 'var(--critical)',
                    borderColor: 'var(--critical)',
                  }}
                  onClick={() => {
                    if (confirmRemove.kind === 'member')
                      onRemoveMemberFromProject(confirmRemove.member);
                    else onCancelInvite(confirmRemove.invite);
                  }}
                  disabled={busy}
                >
                  {busy
                    ? 'Removing…'
                    : confirmRemove.kind === 'member'
                      ? 'Remove from project'
                      : 'Cancel invite'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────
// Row components
// ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Avatar({
  name,
  color,
  pending,
}: {
  name: string;
  color: { bg: string; fg: string };
  pending?: boolean;
}) {
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: color.bg,
        color: color.fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 13,
        flexShrink: 0,
        border: pending ? '1.5px dashed var(--text-tertiary)' : 'none',
      }}
    >
      {initials(name)}
    </div>
  );
}

function MemberRow({
  member,
  canManage,
  onEdit,
}: {
  member: ProjectMember;
  canManage: boolean;
  onEdit: () => void;
}) {
  const color = roleColor(member.role);
  const roleLabel = member.isProjectClient
    ? ROLE_LABELS.client
    : ROLE_LABELS[member.role ?? 'viewer'];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        border: '1px solid var(--border)',
        borderRadius: 8,
        cursor: canManage ? 'pointer' : 'default',
        background: '#fff',
      }}
      onClick={canManage ? onEdit : undefined}
    >
      <Avatar name={member.displayName} color={color} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {member.displayName}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
              background: color.bg,
              color: color.fg,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginRight: 6,
            }}
          >
            {roleLabel}
          </span>
          {formatPhone(member.phoneNumber)}
        </div>
      </div>
      {canManage ? (
        <i
          className="ph-bold ph-caret-right"
          style={{ color: 'var(--text-tertiary)' }}
        />
      ) : (
        <i
          className={
            member.isProjectClient ? 'ph ph-user' : 'ph ph-shield-check'
          }
          style={{ color: color.fg, fontSize: 16 }}
        />
      )}
    </div>
  );
}

function InviteRow({
  invite,
  canManage,
  onEdit,
}: {
  invite: PendingInvite;
  canManage: boolean;
  onEdit: () => void;
}) {
  const color = roleColor(invite.role);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        border: '1px dashed var(--border)',
        borderRadius: 8,
        cursor: canManage ? 'pointer' : 'default',
        background: '#FAFAFA',
      }}
      onClick={canManage ? onEdit : undefined}
    >
      <Avatar
        name={invite.displayName || invite.phoneNumber}
        color={color}
        pending
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {invite.displayName || invite.phoneNumber}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
              background: color.bg,
              color: color.fg,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginRight: 6,
            }}
          >
            {ROLE_LABELS[invite.role]}
          </span>
          {formatPhone(invite.phoneNumber)} · Invited
        </div>
      </div>
      {canManage ? (
        <i
          className="ph-bold ph-caret-right"
          style={{ color: 'var(--text-tertiary)' }}
        />
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Forms
// ────────────────────────────────────────────────────────────

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--text-primary)',
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

function InviteForm({
  onCancel,
  onSubmit,
  busy,
}: {
  onCancel: () => void;
  onSubmit: (args: {
    phoneNumber: string;
    displayName: string;
    role: AssignableRole;
  }) => void;
  busy: boolean;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<AssignableRole>('designer');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = () => {
    setLocalError(null);
    const normalised = normalizeIndianPhoneE164(phone);
    if (!normalised) {
      setLocalError('Enter a valid Indian mobile number.');
      return;
    }
    if (role === 'client' && !name.trim()) {
      setLocalError("Client invites need the person's name.");
      return;
    }
    onSubmit({
      phoneNumber: normalised,
      displayName: name.trim(),
      role,
    });
  };

  return (
    <div
      className="card"
      style={{
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        background: 'var(--surface-muted, #FAFAFA)',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600 }}>Add team member</div>
      <div>
        <label style={LABEL_STYLE}>Name</label>
        <input
          style={INPUT_STYLE}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder="Ananya Prasad"
          autoFocus
        />
      </div>
      <div>
        <label style={LABEL_STYLE}>Phone *</label>
        <input
          style={INPUT_STYLE}
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
          placeholder="+91 98765 43210"
          inputMode="tel"
        />
      </div>
      <div>
        <label style={LABEL_STYLE}>Role on project *</label>
        <RolePicker value={role} onChange={setRole} />
      </div>
      {localError ? (
        <div
          style={{
            fontSize: 12,
            color: 'var(--critical)',
            background: 'var(--critical-bg)',
            padding: 8,
            borderRadius: 4,
          }}
        >
          {localError}
        </div>
      ) : null}
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
      >
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={handleSubmit}
          disabled={busy}
        >
          {busy ? 'Sending…' : 'Send invite'}
        </button>
      </div>
    </div>
  );
}

function RoleEditCard({
  current,
  displayName,
  phone,
  onCancel,
  onSave,
  onRemove,
  busy,
}: {
  current: RoleKey | null;
  displayName: string;
  phone: string;
  onCancel: () => void;
  onSave: (role: AssignableRole) => void;
  onRemove?: () => void;
  busy: boolean;
}) {
  const initial: AssignableRole = (current &&
  (ASSIGNABLE_ROLES as readonly string[]).includes(current)
    ? current
    : 'designer') as AssignableRole;
  const [role, setRole] = useState<AssignableRole>(initial);
  return (
    <div
      className="card"
      style={{
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        background: 'var(--surface-muted, #FAFAFA)',
      }}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{displayName}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {phone}
        </div>
      </div>
      <div>
        <label style={LABEL_STYLE}>Role</label>
        <RolePicker value={role} onChange={setRole} />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {onRemove ? (
          <button
            type="button"
            className="btn-secondary"
            style={{ color: 'var(--critical)' }}
            onClick={onRemove}
            disabled={busy}
          >
            <i className="ph ph-trash" /> Remove
          </button>
        ) : (
          <div />
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onSave(role)}
            disabled={busy || role === current}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RolePicker({
  value,
  onChange,
}: {
  value: AssignableRole;
  onChange: (r: AssignableRole) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 6,
      }}
    >
      {ASSIGNABLE_ROLES.map((r) => {
        const active = r === value;
        const color = roleColor(r);
        return (
          <button
            key={r}
            type="button"
            onClick={() => onChange(r)}
            style={{
              padding: '8px 10px',
              border: active
                ? `1.5px solid ${color.fg}`
                : '1px solid var(--border)',
              borderRadius: 6,
              background: active ? color.bg : '#fff',
              color: active ? color.fg : 'var(--text-primary)',
              fontSize: 12,
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {ROLE_LABELS[r]}
          </button>
        );
      })}
    </div>
  );
}
